import { Alert, Box, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import React, { useCallback, useEffect, useState } from "react";
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";
import { doLogError } from "../app/routes/admin/restQueries";

interface IProps {
  isGlobal?: boolean;
  customComponent?: React.ReactElement;
  identifier?: string;
  children?: React.ReactNode;
}

const ERROR_MODAL_MAX_WIDTH = 760;
const ERROR_MODAL_MIN_WIDTH = 560;
const ERROR_MODAL_WIDTH = `clamp(${ERROR_MODAL_MIN_WIDTH}px, calc(100vw - 64px), ${ERROR_MODAL_MAX_WIDTH}px)`;

const ErrorBoundary: React.FC<IProps> = ({ isGlobal = false, customComponent, identifier, children }) => {
  const [hasError, setHasError] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const errorHappend = useCallback(async (doLog: boolean, nextErrorMessage: string, nextErrorStack: string) => {
    localStorage.clear();

    if (doLog === true) {
      await doLogError(nextErrorMessage, nextErrorStack, window.location.href);
    }
  }, []);

  const resetState = useCallback(() => {
    setHasError(false);
    setExpanded(false);
    setErrorMessage("");
  }, []);

  const unhandledrejectionEventListener = useCallback(
    (promiseError: PromiseRejectionEvent) => {
      console.log("### unhandledrejection");

      if (promiseError.reason?.type === "backend") {
        const backendMessage = JSON.stringify(promiseError.reason.message);
        setHasError(true);
        setErrorMessage(backendMessage);
        // backend errors are logged in backend
        errorHappend(false, null, null);
      } else {
        const promiseMessage = JSON.stringify(promiseError.reason);
        setHasError(true);
        setErrorMessage(promiseMessage);
        errorHappend(true, promiseMessage, "");
      }
    },
    [errorHappend],
  );

  useEffect(() => {
    if (isGlobal !== true) {
      return;
    }

    const previousOnError = window.onerror;
    window.onerror = (event: string | Event, source: string, _lineNo: number, _columnNo: number, error: Error) => {
      console.log("### window.onerror");

      const eventString = typeof event === "string" ? event : event?.toString?.() ?? "";
      if (eventString.startsWith("ResizeObserver loop completed with undelivered notifications")) {
        // Ignore ResizeObserver loop errors
        return;
      }

      const errorMsg = `${error?.message ?? "Error is undefined"}\n${eventString}`;

      // Errors caught by boundaries in dev environment will be re-thrown and caught by window.onerror.
      // No need to show error modal.
      if (import.meta.env.DEV === true) {
        console.error(errorMsg);
        errorHappend(false, null, null);
      } else {
        if (error != null) {
          setHasError(true);
          setErrorMessage(errorMsg);

          errorHappend(true, errorMsg, error.stack);
        }

        errorHappend(true, errorMsg, "");
      }
    };

    window.addEventListener("unhandledrejection", unhandledrejectionEventListener);

    return () => {
      window.onerror = previousOnError;
      window.removeEventListener("unhandledrejection", unhandledrejectionEventListener);
    };
  }, [isGlobal, errorHappend, unhandledrejectionEventListener]);

  const onBoundaryError = useCallback(
    (error: Error, info: { componentStack: string }) => {
      console.log("### componentDidCatch");

      setHasError(true);
      setErrorMessage(`${error.message}\n${info.componentStack}\n-\n${error.stack}`);

      errorHappend(true, error.message, `${info.componentStack}\n\n${error.stack}`);
    },
    [errorHappend],
  );

  const visibleErrorMessage = errorMessage ?? "";
  const hasVisibleErrorMessage = visibleErrorMessage.trim().length > 0;

  const modalOpen = hasError;

  if (customComponent && modalOpen) {
    return customComponent;
  }

  return (
    <>
      <ReactErrorBoundary onError={onBoundaryError} fallbackRender={() => null} resetKeys={[identifier, hasError]}>
        {children}
      </ReactErrorBoundary>

      {modalOpen && (
        <Modal
          opened={modalOpen}
          onClose={resetState}
          title="En uventet feil har oppstått!"
          size={ERROR_MODAL_WIDTH}
          yOffset={180}
          styles={{
            content: { maxHeight: "90vh" },
            body: { paddingTop: 8 },
          }}
        >
          <Stack gap="md">
            <Alert
              variant="light"
              title={
                <Group gap={6} align="center">
                  <IconAlertTriangle size={28} />
                  <Text fw={600} size="md">
                    Teknisk personale er varslet
                  </Text>
                </Group>
              }
            >
              <Text size="sm">
                Kontakt support på <strong>mcapps@multiconsult.no</strong> for ytterligere info.
              </Text>
            </Alert>

            {expanded && (
              <Box
                bg="dark.9"
                c="white"
                p="sm"
                style={{
                  borderRadius: 8,
                  maxHeight: 340,
                  overflow: "auto",
                  fontFamily: "monospace",
                  border: "1px solid rgba(255, 255, 255, 0.18)",
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    color: "#f8f9fa",
                    lineHeight: 1.45,
                  }}
                >
                  {visibleErrorMessage}
                </pre>
              </Box>
            )}

            <Group justify="space-between" align="center" wrap="nowrap">
              <Group>
                {!expanded && hasVisibleErrorMessage && (
                  <Button variant="subtle" onClick={() => setExpanded(true)}>
                    Se teknisk feilmelding
                  </Button>
                )}
              </Group>

              <Group>
                <Button
                  variant="filled"
                  onClick={() => {
                    window.location.href = "/";
                  }}
                >
                  Start programmet på nytt
                </Button>
                <Button variant="default" onClick={resetState}>
                  Prøv igjen
                </Button>
              </Group>
            </Group>
          </Stack>
        </Modal>
      )}
    </>
  );
};

export default ErrorBoundary;
